import { Box, Skeleton, Table } from "@mantine/core"
import { InfoIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { TableActionButton, TableActions } from "../table"
import RenderText from "../ui/render-text"
import { AttributionBadge, ExceptionStatusBadge, ExceptionTypeBadge } from "./atoms"
import type { IDeliveryException } from "../../apis/failed-delivery"

interface Props {
  rows: IDeliveryException[]
  selectedId: string | null
  onSelect: (id: string) => void
  loading?: boolean
}

export function ExceptionsTable({ rows, selectedId, onSelect, loading = false }: Props) {
  const { t } = useTranslation("delivery")
  if (loading) {
    return (
      <Box>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} h={36} mb="xs" radius="sm" />
        ))}
      </Box>
    )
  }

  return (
    <Table highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <RenderText text="#" />
          </Table.Th>
          <Table.Th>
            <RenderText text={t("exTable.type")} />
          </Table.Th>
          <Table.Th>
            <RenderText text={t("exTable.status")} />
          </Table.Th>
          <Table.Th>
            <RenderText text={t("exTable.attribution")} />
          </Table.Th>
          <Table.Th>
            <RenderText text={t("exTable.date")} />
          </Table.Th>
          <Table.Th w={96}>
            <RenderText text={t("exTable.action")} />
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={6}>
              <RenderText
                text={t("exTable.noRecords")}
                color="var(--mantine-color-dimmed)"
                style={{ textAlign: "center", padding: "var(--mantine-spacing-md)" }}
              />
            </Table.Td>
          </Table.Tr>
        ) : (
          rows.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() => onSelect(row.id)}
              style={{
                backgroundColor:
                  selectedId === row.id ? "var(--mantine-color-brand-0)" : undefined,
                cursor: "pointer",
              }}
            >
              <Table.Td>
                <RenderText text={row.attemptNumber} />
              </Table.Td>
              <Table.Td>
                <ExceptionTypeBadge type={row.exceptionType} />
              </Table.Td>
              <Table.Td>
                <ExceptionStatusBadge status={row.status} />
              </Table.Td>
              <Table.Td>
                <AttributionBadge attribution={row.attribution} />
              </Table.Td>
              <Table.Td>
                <RenderText text={dayjs(row.raisedAt).format("YYYY-MM-DD HH:mm")} />
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                <TableActions>
                  <TableActionButton
                    icon={<InfoIcon />}
                    label={t("exTable.detail")}
                    onClick={() => onSelect(row.id)}
                  />
                </TableActions>
              </Table.Td>
            </Table.Tr>
          ))
        )}
      </Table.Tbody>
    </Table>
  )
}
